// src/Team.js
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Footer from './Footer';

const members = [
  {
    name: 'Ansh Chaudhary',
    role: 'Growth & Product',
    photo: '/ansh-photo.jpeg',
    bio: 'Ansh bridges the gap between engineering and the community. He balances technical contributions with managing the social media presence and direct user engagement.',
    credentials: [
      'NSUT, Delhi (2024–28)',
      'D\'Code NSUT (Competitive Programming)',
      'Debating Society of NSUT',
    ],
    highlights: [
      {
        title: 'D\'Code NSUT',
        desc: 'Active member of the competitive programming team, focusing on algorithmic problem solving.',
      },
      {
        title: 'NSUT Debating Society',
        desc: 'Member of the Model United Nations and Parliamentary Debating department.',
      },
    ],
    linkedin: 'https://www.linkedin.com/in/ansh-chaudhary-451898349/',
    github: 'https://github.com/Ansh-12345',
  },
  {
    name: 'Ayush Chaudhary',
    role: 'Founder & Lead Developer',
    photo: '/ayush-photo.png',
    bio: 'Ayush is the creator of Panda AI. He built the first version as a side project and grew it into a product used by hundreds of thousands of Android users. He leads product vision, core AI engineering, and the overall roadmap.',
    credentials: [
      'B.Tech — IIT Roorkee (2021–25)',
      'SDE Intern — True Beacon',
      'Backend Intern — Triomics',
      'Vice Chair — ACM Student Chapter, IITR',
      'Developer — MDG Space, IITR',
    ],
    highlights: [
      {
        title: 'Real-Time Options Analytics',
        desc: 'Built a system in GoLang to compute Greek values, reducing calculation time from 1000ms to 50ms at True Beacon.',
      },
      {
        title: 'Purenet Browser',
        desc: 'Designed and implemented a browser filtering system to curate web content at MDG Space, IITR.',
      },
    ],
    linkedin: 'https://www.linkedin.com/in/ayush0chaudhary/',
    twitter: 'https://x.com/Ayush0Chaudhary',
    github: 'https://github.com/Ayush0Chaudhary',
  },
  {
    name: 'Usman Khatri',
    role: 'Software Engineer',
    photo: '/usman-photo.jpeg',
    bio: 'Usman is a Software Development Engineer at HiLabs. An IIT Roorkee graduate, he builds large-scale data transformation engines and secure financial APIs. He drives core system architecture and cloud integration.',
    credentials: [
      'B.Tech — IIT Roorkee',
      'Software Engineer — HiLabs',
      'Backend Engineer — Curie Money',
      'Software Developer — blinkX',
      'Full Stack Developer — AiProff',
    ],
    highlights: [
      {
        title: 'Curie Money Backend',
        desc: 'Developed backend systems using Spring Boot and Swagger API during a 7-month intensive internship.',
      },
      {
        title: 'blinkX Trading Platform',
        desc: 'Resolved UI/UX and Auth bugs; developed AppAdmin for managing logs and caches using Flutter/Bloc.',
      },
      {
        title: 'Session Mate App',
        desc: 'Developed and published a full-stack location-based app to App Store and Play Store at AiProff.',
      },
    ],
    linkedin: 'https://www.linkedin.com/in/usman-khatri-92aa94223/',
    twitter: 'https://x.com/camelCase01',
    github: 'https://github.com/camelCase02',
  },
];

const Team = () => {
  return (
    <div className="team-page">
      {/* Header */}
      <section className="tp-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="back-link-wrapper">
              <Link to="/" className="back-link">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><polyline points="12 19 5 12 12 5" /></svg>
                Back to Home
              </Link>
            </div>
            <h1 className="tp-title">Meet the <span>Team</span></h1>
            <p className="tp-subtitle">The people building the future of AI on Android.</p>
          </motion.div>
        </div>
      </section>

      {/* Mission / Product Stage */}
      <section className="tp-mission-section">
        <div className="container">
          <motion.div
            className="tp-mission-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <div className="tp-mission-header">
              <div className="tp-stage-badge">
                <span className="tp-stage-dot" />
                Live on Google Play
              </div>
              <div className="tp-stage-badge tp-stage-badge-alt">
                150K+ Users
              </div>
            </div>
            <h2 className="tp-mission-title">Our Mission</h2>
            <p className="tp-mission-text">
              Panda AI is building the most accessible AI assistant for Android — bringing Gemini-powered conversations, text-to-speech,
              and multi-model intelligence to users worldwide. Our team combines deep experience in GenAI execution,
              real-time systems engineering, and large-scale data platforms to deliver a production-grade product that's already
              serving hundreds of thousands of users.
            </p>
            <div className="tp-tech-tags">
              <span className="tp-tech-tag">Google Cloud</span>
              <span className="tp-tech-tag">Gemini API</span>
              <span className="tp-tech-tag">Android / Kotlin</span>
              <span className="tp-tech-tag">Text-to-Speech</span>
              <span className="tp-tech-tag">Node.js</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cards */}
      <section className="tp-cards-section">
        <div className="container">
          <div className="tp-grid">
            {members.map((m, i) => (
              <motion.div
                key={m.name}
                className="tp-card"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
              >
                <div className="tp-avatar">
                  {m.photo ? (
                    <img src={m.photo} alt={m.name} />
                  ) : (
                    <div className="tp-avatar-initials">
                      <span>{m.initials}</span>
                    </div>
                  )}
                </div>
                <h2 className="tp-name">{m.name}</h2>
                <p className="tp-role">{m.role}</p>
                <p className="tp-bio">{m.bio}</p>

                {/* Credentials */}
                {m.credentials && m.credentials.length > 0 && (
                  <div className="tp-credentials">
                    {m.credentials.map((c, ci) => (
                      <span key={ci} className="tp-credential-tag">{c}</span>
                    ))}
                  </div>
                )}

                {/* Experience Highlights */}
                {m.highlights && m.highlights.length > 0 && (
                  <div className="tp-highlights">
                    <p className="tp-highlights-label">Experience</p>
                    {m.highlights.map((h, hi) => (
                      <div key={hi} className="tp-highlight-item">
                        <p className="tp-highlight-title">{h.title}</p>
                        <p className="tp-highlight-desc">{h.desc}</p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="tp-socials">
                  {m.linkedin && (
                    <a
                      href={m.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tp-social-link"
                      aria-label={`${m.name} on LinkedIn`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                      <span>LinkedIn</span>
                    </a>
                  )}
                  {m.twitter && (
                    <a
                      href={m.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tp-social-link"
                      aria-label={`${m.name} on X`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                      <span>X</span>
                    </a>
                  )}
                  {m.github && (
                    <a
                      href={m.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tp-social-link"
                      aria-label={`${m.name} on GitHub`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                      </svg>
                      <span>GitHub</span>
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        .team-page {
          min-height: 100vh;
          background: #000;
          color: var(--text-primary);
          font-family: 'Inter', sans-serif;
        }

        .tp-hero {
          padding: 120px 0 60px;
          background: #000;
          border-bottom: 1px solid rgba(255,255,255,0.07);
        }

        .back-link-wrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 32px;
        }

        .back-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 24px;
          border-radius: 100px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          color: var(--text-secondary);
          text-decoration: none;
          font-weight: 500;
          font-size: 0.9rem;
          transition: all 0.3s ease;
          backdrop-filter: blur(8px);
        }

        .back-link:hover {
          color: white;
          background: rgba(255,255,255,0.12);
          border-color: rgba(255,255,255,0.25);
          transform: translateX(-4px);
        }

        .back-link svg {
          transition: transform 0.3s ease;
        }

        .back-link:hover svg {
          transform: translateX(-3px);
        }

        .tp-title {
          font-size: 3rem;
          font-weight: 700;
          color: white;
          line-height: 1.15;
          margin-bottom: 16px;
        }

        .tp-title span {
          background: linear-gradient(135deg, #8b5cf6, #3b82f6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .tp-subtitle {
          font-size: 1.15rem;
          color: var(--text-secondary);
          max-width: 540px;
        }

        /* Mission Section */
        .tp-mission-section {
          padding: 60px 0;
          background: #000;
        }

        .tp-mission-card {
          max-width: 800px;
          margin: 0 auto;
          background: rgba(139,92,246,0.04);
          border: 1px solid rgba(139,92,246,0.18);
          border-radius: 20px;
          padding: 40px 36px;
        }

        .tp-mission-header {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 24px;
        }

        .tp-stage-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 16px;
          border-radius: 100px;
          background: rgba(34,197,94,0.12);
          border: 1px solid rgba(34,197,94,0.3);
          color: #4ade80;
          font-size: 0.85rem;
          font-weight: 600;
          letter-spacing: 0.02em;
        }

        .tp-stage-badge-alt {
          background: rgba(59,130,246,0.12);
          border-color: rgba(59,130,246,0.3);
          color: #60a5fa;
        }

        .tp-stage-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #4ade80;
          animation: pulse-dot 2s infinite;
        }

        @keyframes pulse-dot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }

        .tp-mission-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
          margin-bottom: 12px;
        }

        .tp-mission-text {
          font-size: 1rem;
          line-height: 1.7;
          color: var(--text-secondary);
          margin-bottom: 20px;
        }

        .tp-tech-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .tp-tech-tag {
          padding: 4px 14px;
          border-radius: 8px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          color: rgba(255,255,255,0.7);
          font-size: 0.8rem;
          font-weight: 500;
        }

        /* Cards */
        .tp-cards-section {
          padding: 40px 0 120px;
          background: #000;
        }

        .tp-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 32px;
          max-width: 1100px;
          margin: 0 auto;
        }

        .tp-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 20px;
          padding: 40px 28px;
          text-align: center;
          backdrop-filter: blur(10px);
          transition: border-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .tp-card:hover {
          border-color: rgba(139,92,246,0.45);
          transform: translateY(-6px);
          box-shadow: 0 24px 48px rgba(0,0,0,0.4);
        }

        .tp-avatar {
          width: 110px;
          height: 110px;
          border-radius: 50%;
          margin: 0 auto 20px;
          overflow: hidden;
          border: 2px solid rgba(139,92,246,0.4);
        }

        .tp-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .tp-avatar-initials {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, rgba(139,92,246,0.35), rgba(59,130,246,0.35));
        }

        .tp-avatar-initials span {
          font-size: 2rem;
          font-weight: 700;
          color: white;
        }

        .tp-name {
          font-size: 1.35rem;
          font-weight: 600;
          color: white;
          margin-bottom: 6px;
        }

        .tp-role {
          font-size: 0.9rem;
          color: #8b5cf6;
          font-weight: 500;
          margin-bottom: 16px;
          letter-spacing: 0.02em;
        }

        .tp-bio {
          font-size: 0.95rem;
          line-height: 1.65;
          color: var(--text-secondary);
        }

        /* Credentials */
        .tp-credentials {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 6px;
          margin-top: 16px;
        }

        .tp-credential-tag {
          padding: 4px 12px;
          border-radius: 8px;
          background: rgba(139,92,246,0.1);
          border: 1px solid rgba(139,92,246,0.2);
          color: #c4b5fd;
          font-size: 0.78rem;
          font-weight: 500;
        }

        /* Experience Highlights */
        .tp-highlights {
          width: 100%;
          margin-top: 20px;
          text-align: left;
        }

        .tp-highlights-label {
          font-size: 0.78rem;
          font-weight: 600;
          color: rgba(255,255,255,0.4);
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 10px;
        }

        .tp-highlight-item {
          padding: 12px 14px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 12px;
          margin-bottom: 8px;
        }

        .tp-highlight-item:last-child {
          margin-bottom: 0;
        }

        .tp-highlight-title {
          font-size: 0.85rem;
          font-weight: 600;
          color: white;
          margin-bottom: 4px;
        }

        .tp-highlight-desc {
          font-size: 0.82rem;
          line-height: 1.5;
          color: var(--text-secondary);
        }

        /* Socials */
        .tp-socials {
          display: flex;
          justify-content: center;
          gap: 10px;
          margin-top: 18px;
          flex-wrap: wrap;
        }

        .tp-social-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 18px;
          border-radius: 10px;
          background: rgba(139,92,246,0.12);
          border: 1px solid rgba(139,92,246,0.25);
          color: #a78bfa;
          text-decoration: none;
          font-size: 0.88rem;
          font-weight: 500;
          transition: background 0.25s ease, color 0.25s ease, border-color 0.25s ease;
        }

        .tp-social-link:hover {
          background: rgba(139,92,246,0.25);
          color: white;
          border-color: rgba(139,92,246,0.5);
        }

        .tp-social-link svg {
          flex-shrink: 0;
        }

        @media (max-width: 768px) {
          .tp-title { font-size: 2.2rem; }
          .tp-grid { grid-template-columns: 1fr; max-width: 420px; }
          .tp-hero { padding: 100px 0 48px; }
          .tp-mission-card { padding: 28px 20px; }
          .tp-mission-title { font-size: 1.3rem; }
        }
      `}</style>
    </div>
  );
};

export default Team;
